<template>
    <div class="reactivation-course" :class="{ 'is-selected': selection.selected }">
        <div class="information">
            <div class="columns is-mobile">
                <div class="column is-narrow">
                    <b-checkbox v-model="selection.selected" size="is-medium" @input="emitUpdate"></b-checkbox>
                </div>
                <div class="column">
                    <div class="course-title">
                        {{ course.name }}
                    </div>
                    <div class="custom-name" v-if="course.customName">
                        {{ course.customName }}
                    </div>

                    <!-- Progress Bar -->
                    <div class="progress-section mt-3">
                        <div class="progress-info">
                            <span class="progress-label">
                                <i class="fas fa-clock mr-2"></i>
                                {{ course.completedHours }} / {{ course.originalUnits }} hours completed
                            </span>
                            <span class="progress-percent">{{ progressPercent }}%</span>
                        </div>
                        <progress class="progress is-primary is-small" :value="course.completedHours" :max="course.originalUnits"></progress>
                    </div>

                    <!-- Badges -->
                    <div class="badges mt-3">
                        <span v-if="course.inGracePeriod" class="tag is-warning">
                            <i class="fas fa-clock mr-1"></i>Grace Period
                        </span>
                        <span v-if="course.requiresNewEnrollment" class="tag is-info">
                            <i class="fas fa-refresh mr-1"></i>New Enrollment
                        </span>
                        <span class="tag is-light">
                            Expired {{ course.daysExpired }} days ago
                        </span>
                    </div>

                    <!-- Grace Period Explanation -->
                    <div v-if="course.inGracePeriod" class="grace-period-note mt-3">
                        <i class="fas fa-info-circle mr-2"></i>
                        <span>
                            This course is in its <strong>grace period</strong> — it expired within the last 14 days, so you can reactivate it at <strong>no charge</strong> regardless of how many hours you select. The course will be extended for an additional 14 days.
                        </span>
                    </div>

                    <!-- Hour Selection Options (only when selected) -->
                    <div v-if="selection.selected" class="hour-options mt-4">
                        <div class="option-title mb-3">Select reactivation hours:</div>

                        <!-- Full Hours Option -->
                        <div class="option-row" @click="selectOption('full')">
                            <b-radio v-model="selection.hoursType" native-value="full" @input="emitUpdate">
                                Full Course ({{ course.originalUnits }} hours)
                            </b-radio>
                            <span class="option-price" :class="{ 'is-free': calculatePrice('full') === 0 }">
                                {{ priceLabel('full') }}
                            </span>
                        </div>

                        <!-- Partial Hours Option -->
                        <div class="option-row" @click="selectOption('partial')">
                            <b-radio v-model="selection.hoursType" native-value="partial" @input="emitUpdate">
                                Partial Hours
                            </b-radio>
                            <span class="option-price" :class="{ 'is-free': calculatePrice('partial') === 0 }">
                                {{ priceLabel('partial') }}
                            </span>
                        </div>

                        <!-- Partial Hours Slider (shown when partial selected) -->
                        <div v-if="selection.hoursType === 'partial'" class="partial-selector ml-5 mt-2 mb-3">
                            <div class="columns is-mobile is-vcentered">
                                <div class="column">
                                    <b-slider v-model="selection.hours" :min="minHours" :max="course.originalUnits" :step="1" @input="emitUpdate">
                                        <template v-for="val in sliderTicks">
                                            <b-slider-tick :value="val" :key="val">{{ val }}</b-slider-tick>
                                        </template>
                                    </b-slider>
                                </div>
                                <div class="column is-narrow">
                                    <b-input v-model.number="selection.hours" type="number" :min="minHours" :max="course.originalUnits" size="is-small" style="width: 80px" @input="emitUpdate"></b-input>
                                </div>
                            </div>
                        </div>

                        <!-- Completed Hours Only Option (requires at least 1 completed hour) -->
                        <div v-if="course.completedHours > 0 && course.completedHours < 1" class="option-note mt-2">
                            <i class="fas fa-info-circle mr-1"></i>
                            Free reactivation of completed hours requires at least 1 hour of progress ({{ course.completedHours }} completed).
                        </div>
                        <div v-if="course.completedHours >= 1" class="option-row" @click="selectOption('completed')">
                            <b-radio v-model="selection.hoursType" native-value="completed" @input="emitUpdate">
                                Completed Hours Only ({{ Math.ceil(course.completedHours) }} hours)
                            </b-radio>
                            <span class="option-price is-free">
                                FREE
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "ReactivationCourse",
    filters: {
        currency(value) {
            if (typeof value !== 'number') value = parseFloat(value) || 0;
            return '$' + value.toFixed(2);
        }
    },
    props: {
        course: {
            type: Object,
            required: true
        },
        pricePerHour: {
            type: Number,
            default: 5
        },
        value: {
            type: Object,
            default: () => ({
                selected: false,
                hours: 0,
                hoursType: 'full'
            })
        }
    },
    data() {
        return {
            selection: {
                selected: this.value.selected || false,
                hours: this.value.hours || this.course.originalUnits,
                hoursType: this.value.hoursType || 'full'
            }
        };
    },
    computed: {
        progressPercent() {
            if (!this.course.originalUnits) return 0;
            return Math.round((this.course.completedHours / this.course.originalUnits) * 100);
        },
        minHours() {
            // Minimum hours is the completed hours (can't go below what they've done)
            return Math.ceil(this.course.completedHours) || 1;
        },
        sliderTicks() {
            const ticks = [];
            const max = this.course.originalUnits;
            const step = Math.ceil(max / 5);
            for (let i = 0; i <= max; i += step) {
                ticks.push(i);
            }
            if (ticks[ticks.length - 1] !== max) {
                ticks.push(max);
            }
            return ticks;
        }
    },
    watch: {
        value: {
            handler(newVal) {
                this.selection = { ...newVal };
            },
            deep: true
        },
        'selection.hoursType': function(newType) {
            // Update hours when type changes
            if (newType === 'full') {
                this.selection.hours = this.course.originalUnits;
            } else if (newType === 'completed') {
                this.selection.hours = Math.ceil(this.course.completedHours);
            } else if (newType === 'partial') {
                // Keep current or set to original if not set
                if (!this.selection.hours || this.selection.hours > this.course.originalUnits) {
                    this.selection.hours = this.course.originalUnits;
                }
            }
            this.emitUpdate();
        }
    },
    methods: {
        selectOption(type) {
            this.selection.hoursType = type;
        },
        calculatePrice(type) {
            const completedHours = this.course.completedHours || 0;
            let paidHours = 0;

            if (type === 'completed') {
                return 0; // Always free
            } else if (this.course.inGracePeriod) {
                return 0; // Grace period: all hours free
            } else if (type === 'full') {
                paidHours = Math.max(0, this.course.originalUnits - completedHours);
            } else if (type === 'partial') {
                paidHours = Math.max(0, this.selection.hours - completedHours);
            }

            return paidHours * this.pricePerHour;
        },
        priceLabel(type) {
            if (this.course.inGracePeriod && type !== 'completed') {
                return 'FREE (Grace Period)';
            }
            const price = this.calculatePrice(type);
            if (price === 0) return 'FREE';
            return '$' + price.toFixed(2);
        },
        emitUpdate() {
            this.$emit('input', {
                enrollmentId: this.course.id,
                selected: this.selection.selected,
                hours: this.selection.hours,
                hoursType: this.selection.hoursType,
                daysExpired: this.course.daysExpired,
                inGracePeriod: this.course.inGracePeriod
            });
        }
    },
    mounted() {
        // Initialize hours based on type
        if (this.selection.hoursType === 'full') {
            this.selection.hours = this.course.originalUnits;
        } else if (this.selection.hoursType === 'completed') {
            this.selection.hours = Math.ceil(this.course.completedHours);
        }
    }
};
</script>

<style lang="scss" scoped>
.reactivation-course {
    background-color: white;
    border-radius: 4px;
    box-shadow: 5px 5px 30px 0 rgba(0, 0, 0, 0.4);

    .information {
        padding: 1.5rem;
    }

    .course-title {
        font-weight: 600;
        color: #1e3a5f;
        font-size: 1.1rem;
    }

    .custom-name {
        font-weight: normal;
        font-size: 0.85rem;
        color: #737881;
        margin-top: 0.25rem;
    }

    .progress-section {
        .progress-info {
            display: flex;
            justify-content: space-between;
            font-size: 0.85rem;
            color: #666;
            margin-bottom: 0.25rem;
        }

        .progress {
            height: 8px;
        }
    }

    .badges {
        .tag {
            margin-right: 0.5rem;
        }
    }

    .grace-period-note {
        background-color: #fff8e1;
        border-left: 3px solid #ffb300;
        border-radius: 4px;
        padding: 0.75rem 1rem;
        font-size: 0.9rem;
        color: #6b5300;

        i {
            color: #ffb300;
        }
    }

    .hour-options {
        background-color: #f8f9fa;
        border-radius: 4px;
        padding: 1rem;

        .option-title {
            font-weight: 600;
            color: #363636;
        }

        .option-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.5rem 0;
            cursor: pointer;

            &:hover {
                background-color: #eef1f5;
                margin: 0 -0.5rem;
                padding: 0.5rem;
                border-radius: 4px;
            }
        }

        .option-price {
            font-weight: 600;
            color: #363636;

            &.is-free {
                color: #00d1b2;
            }
        }

        .partial-selector {
            background-color: #fff;
            padding: 1rem;
            border-radius: 4px;
            border: 1px solid #e7e7e7;
        }
    }
}
</style>
